/**
 * efflux-embed-core — Protocolo comando/evento (contrato compartilhado)
 * =====================================================================
 *
 * Fonte ÚNICA da verdade do canal host ↔ Efflux (§4 do FEATURE-EMBEDDED-ANDROID).
 * Este arquivo (TS) é o contrato canônico; o espelho Kotlin
 * (`efflux-embedded-test`) e, no futuro, o `postMessage` da web (§10) usam o
 * MESMO envelope e os MESMOS nomes.
 *
 * Transporte-agnóstico de propósito:
 * - Android: host→web via `window.__efflux.dispatch(json)`; web→host via
 *   `window.EffluxHost.emit(json)` (@JavascriptInterface).
 * - Web (futuro): `postMessage` com o mesmo shape.
 *
 * Envelope versionado (`v`) → dá pra evoluir sem quebrar hosts antigos (D-§4).
 */

/** Versão do protocolo. Bump SÓ em mudança incompatível de shape. */
export const PROTOCOL_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Comandos (Host → Efflux)
// ---------------------------------------------------------------------------

export type CommandType =
  | 'start'
  | 'stop'
  | 'configure'
  | 'hide'
  | 'unhide'
  | 'setBounds'
  | 'setVolume'
  | 'releaseVolume'
  | 'reload'
  | 'captureCoverFrame';

/**
 * `start` — inicializa e começa o loop. `tags[]` → sync → admin (D9/§16).
 *
 * SEM `externalId`: o external_id é auto-gerado pelo player (nunca escolhido pelo
 * host — durável, ANDROID_ID) e volta pro host no evento `ready`. Rótulos do host
 * viajam só em `tags[]` (o AAR mescla `tag`+`tags` do EffluxConfig nesse array).
 */
export interface StartPayload {
  tenant: string;
  tags?: string[];
  apiUrl?: string;
  interactive?: boolean;
  /** Fase 3 usa de verdade; na Fase 2 é aceito e ignorado (só quem move é a View). */
  initialBounds?: BoundsPayload;
}

/** `configure` — patch de config sem reiniciar o loop. */
export interface ConfigurePayload {
  volumeLevel?: number;
  brightnessLevel?: number;
  brightnessAuto?: boolean;
  interactive?: boolean;
  [key: string]: unknown;
}

/** `setBounds` — informativo p/ web; no Android quem move é a View (Fase 3). */
export type BoundsPayload =
  | { px: { x: number; y: number; width: number; height: number } }
  | { percent: { x: number; y: number; width: number; height: number } };

export interface SetVolumePayload {
  level: number;
}

export interface CommandPayloadMap {
  start: StartPayload;
  stop: undefined;
  configure: ConfigurePayload;
  hide: undefined;
  unhide: undefined;
  setBounds: BoundsPayload;
  setVolume: SetVolumePayload;
  /**
   * `releaseVolume` — host devolve o controle do volume ao servidor Efflux
   * (regra B2B host-owned volume). Depois de `setVolume`, o volume fica sticky
   * no host; este comando reabre o controle server-side (volta a aplicar a
   * config do player). Sem payload.
   */
  releaseVolume: undefined;
  reload: undefined;
  /**
   * `captureCoverFrame` — Path 3 (recycle proativo do WebView, anti-crash de
   * address-space no renderer 32-bit). O host pede ao player pra capturar o frame
   * ATUAL (DOM+vídeo, mesma via do screenshot do admin) e devolvê-lo via
   * `window.EffluxHost.onCoverFrame(base64)`. O host mostra esse frame como cover
   * enquanto recria a WebView (reset do address space) → o cliente não vê preto.
   * Sem payload.
   */
  captureCoverFrame: undefined;
}

// ---------------------------------------------------------------------------
// Eventos (Efflux → Host)
// ---------------------------------------------------------------------------

export type EventType =
  | 'ready'
  | 'started'
  | 'stopped'
  | 'error'
  | 'contentChanged'
  | 'identityConflict'
  | 'heartbeat'
  | 'volumeChanged'
  | 'connectivity';

export interface ReadyPayload {
  externalId?: string;
  version: string;
  platform: 'embedded';
}

export interface ErrorPayload {
  code: string;
  message: string;
}

export interface ContentChangedPayload {
  libraryHash: string;
}

export interface IdentityConflictPayload {
  externalId: string;
}

/**
 * `heartbeat` — sinal de vida periódico do player (Fase 9 — self-healing). O host
 * (AAR) arma um watchdog: se parar de receber heartbeat por N segundos, a WebView
 * congelou (renderer vivo mas travado) → o AAR faz reload/recria. `ts` = epoch ms
 * do player no instante do batimento (o AAR mede frescor/latência).
 */
export interface HeartbeatPayload {
  ts: number;
}

/**
 * `volumeChanged` — o volume efetivo do player mudou. `source='server'` quando
 * veio da config do servidor (Efflux no controle); `source='host'` quando o host
 * assumiu via `setVolume` (host-owned volume). O host usa pra exibir o volume
 * atual e QUEM controla — sem precisar consultar. Emitido no boot (estado inicial),
 * em `setVolume` (host) e em `releaseVolume` (volta ao valor do servidor).
 */
export interface VolumeChangedPayload {
  level: number;
  source: 'server' | 'host';
}

/**
 * `connectivity` — o estado de conexão do player com o servidor do Efflux mudou.
 * `online=true` quando o servidor está alcançável (sync/registro funcionam);
 * `online=false` quando está offline (o player segue tocando do cache). O host
 * usa pra exibir "conectado ao servidor" vs "offline". Emitido no boot (estado
 * inicial) e a cada mudança de conectividade.
 */
export interface ConnectivityPayload {
  online: boolean;
}

export interface EventPayloadMap {
  ready: ReadyPayload;
  started: undefined;
  stopped: undefined;
  error: ErrorPayload;
  contentChanged: ContentChangedPayload;
  identityConflict: IdentityConflictPayload;
  heartbeat: HeartbeatPayload;
  volumeChanged: VolumeChangedPayload;
  connectivity: ConnectivityPayload;
}

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

/**
 * Envelope idempotente e versionado. `id` permite dedup no receptor; `v` permite
 * um host antigo/novo negociar compatibilidade sem quebrar.
 */
export interface Envelope<TType extends string = string, TPayload = unknown> {
  v: number;
  id: string;
  type: TType;
  payload?: TPayload;
}

export type CommandEnvelope<T extends CommandType = CommandType> = Envelope<
  T,
  CommandPayloadMap[T]
>;

export type EventEnvelope<T extends EventType = EventType> = Envelope<
  T,
  EventPayloadMap[T]
>;

const COMMAND_TYPES: ReadonlySet<string> = new Set<CommandType>([
  'start',
  'stop',
  'configure',
  'hide',
  'unhide',
  'setBounds',
  'setVolume',
  'releaseVolume',
  'reload',
  'captureCoverFrame',
]);

const EVENT_TYPES: ReadonlySet<string> = new Set<EventType>([
  'ready',
  'started',
  'stopped',
  'error',
  'contentChanged',
  'identityConflict',
  'heartbeat',
  'volumeChanged',
  'connectivity',
]);

export function isCommandType(type: unknown): type is CommandType {
  return typeof type === 'string' && COMMAND_TYPES.has(type);
}

export function isEventType(type: unknown): type is EventType {
  return typeof type === 'string' && EVENT_TYPES.has(type);
}

/**
 * ID de envelope. Determinístico o suficiente pra dedup sem depender de
 * `Math.random`/`crypto` (que não existem em todo runtime de WebView antigo):
 * contador monotônico + prefixo. Não é segredo — só correlação.
 */
let _seq = 0;
export function nextEnvelopeId(prefix = 'evt'): string {
  _seq += 1;
  return `${prefix}-${_seq}`;
}

/** Monta um envelope de evento já com `v`/`id`. */
export function makeEvent<T extends EventType>(
  type: T,
  payload?: EventPayloadMap[T],
): EventEnvelope<T> {
  return { v: PROTOCOL_VERSION, id: nextEnvelopeId('efflux'), type, payload };
}

/**
 * Faz o parse + validação de um envelope de COMANDO cru (string JSON ou objeto).
 * Retorna o envelope tipado, ou `null` se malformado/tipo desconhecido — o
 * receptor NUNCA deve confiar cegamente no que vem do host (R2.3 segurança).
 */
export function parseCommandEnvelope(raw: unknown): CommandEnvelope | null {
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== 'object') return null;

  const env = obj as Record<string, unknown>;
  if (typeof env.v !== 'number') return null;
  if (typeof env.id !== 'string' || env.id.length === 0) return null;
  if (!isCommandType(env.type)) return null;

  return {
    v: env.v,
    id: env.id,
    type: env.type,
    payload: env.payload as CommandPayloadMap[CommandType],
  } as CommandEnvelope;
}
